import { useCallback, useEffect, useMemo, useState } from "react";
import type { BillOut } from "../../../../../api/bills";
import type { SchemaElement } from "../../../../../api/restaurantSchema";
import { useIsEdgeLocal } from "../../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../../store/hooks";
import type { OrderingFloor } from "./useOrderingFloor";
import type { SchemaWaiterChoice } from "./useSchemaWaiterFilter";
import {
  billsOnTable,
  isClosedEmptyBill,
  preferredBillOf,
  tableIdsOfBill,
  type SchemaTableRef,
} from "./orderingFloorData";

interface SchemaSelectionOptions {
  /** Режимът „Схема" е на екрана с масите. */
  active: boolean;
  floor: OrderingFloor;
  /** Подредени като в списъка (вкл. закритите без бон по настройката). */
  sortedBills: BillOut[];
  closingBillIds: ReadonlySet<number>;
  selectedBill: BillOut | null;
  /** Избор на сметка (или на нищо) с опресняване — като клик в списъка. */
  onSelectBill: (billId: number | null) => void;
  /** Само смяна на избора, без опресняване (автоматичният избор). */
  onSyncBill: (billId: number) => void;
  /** Избор на сметката + влизане в менюто („Добави"). */
  onOpenBill: (billId: number) => void;
  /** Свободна маса от схемата → „Нова маса" с името ѝ. */
  onOpenNamedTable: (name: string) => void;
  canOpenBill: boolean;
  /** Полето „Сервитьор" (горе вдясно); null — няма го. */
  waiterChoice: SchemaWaiterChoice | null;
  /** Сервитьорът на фокус (полето или влезлият). */
  focusWaiterId: number | null;
  /** Обща номерация — смяната на фокуса не маха избраната маса. */
  sharedNumbering: boolean;
}

/**
 * Изборът в режим „Схема". Сметката остава в Redux (`selectedBillId` — на него
 * стъпват футърът, модалите и панелът); тук е ФИЗИЧЕСКАТА маса — нужна и за
 * свободна маса без сметка — и районът на екрана. Живее в `Ordering`, за да
 * оцелее при отиване в менюто и обратно.
 *
 * - Клик по маса → сметката ѝ (отворена, първо своята — `preferredBillOf`) или
 *   „свободна маса"; винаги с опресняване на сметките и резервациите.
 * - Двоен клик → „Добави" за сметката ѝ, а на свободна — „Нова маса" с името.
 * - Маса, заета от невидима чужда сметка (обща номерация, без право да виждаш
 *   сметките на колегите — `hiddenOpenTableOf`), е заета: клик я избира (в
 *   панела — само чия е), двоен клик — само избор, „Нова маса" е заключен.
 * - Смени ли се сметката отвън (поръчка, „Настани", глас, „Смяна на маса") →
 *   масата и районът я следват; временна маса → без маса от схемата.
 * - Нефискализираната (закрита без бон) не е на маса от схемата — избира се от
 *   сивата лента, като картата ѝ в списъка; масата не свети.
 * - Избрана маса без сметка, на която се появи отворена (друг терминал, връщане
 *   от менюто) → сметката се избира сама.
 */
export const useSchemaSelection = ({
  active,
  floor,
  sortedBills,
  closingBillIds,
  selectedBill,
  onSelectBill,
  onSyncBill,
  onOpenBill,
  onOpenNamedTable,
  canOpenBill,
  waiterChoice,
  focusWaiterId,
  sharedNumbering,
}: SchemaSelectionOptions) => {
  const currentUserId = useAppSelector((state) => state.auth.userId);
  // В локален режим `reservation` по сметката е остаряло копие (docs/edge.md).
  const edgeLocal = useIsEdgeLocal();
  const { index, regions, linkedBillIdsOn, hiddenOpenTableOf } = floor;
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  // Лична номерация: друг сервитьор на фокус (изборът му, връщане на влезлия,
  // когато показаният остане без маси) — избраната маса се маха, иначе би си
  // избрала сметката на новия със същото име (две „1"). Изчислено в рендера.
  const [lastFocusWaiterId, setLastFocusWaiterId] = useState(focusWaiterId);
  if (lastFocusWaiterId !== focusWaiterId) {
    setLastFocusWaiterId(focusWaiterId);
    if (!sharedNumbering) setSelectedTableId(null);
  }
  const [regionId, setRegionId] = useState<number | null>(null);

  const tableIdsOf = useCallback(
    (bill: BillOut) =>
      tableIdsOfBill(edgeLocal ? { ...bill, reservation: null } : bill, index),
    [edgeLocal, index],
  );

  // Сметките на масата: по името ѝ и тези, които я държат без него (на
  // настанената резервация, „Една сметка" на няколко маси).
  const billsOf = useCallback(
    (table: SchemaElement) =>
      billsOnTable(table, sortedBills, linkedBillIdsOn({ id: table.id, name: table.name ?? "" })),
    [linkedBillIdsOn, sortedBills],
  );

  const takenByHiddenBill = useCallback(
    (table: SchemaElement) => hiddenOpenTableOf({ id: table.id, name: table.name ?? "" }) !== null,
    [hiddenOpenTableOf],
  );

  const tableRefById = useMemo(() => {
    const byId = new Map<number, SchemaTableRef>();
    for (const ref of index.values()) byId.set(ref.table.id, ref);
    return byId;
  }, [index]);

  const selectedBillId = selectedBill?.id ?? null;
  const selectedBillName = selectedBill?.name ?? null;
  // Сметка, закрита без бон на място и показана в сивата лента (закриване без
  // бон при показани нефискализирани) — масата ѝ от схемата се пуска. Скрита
  // (при поискване, без ALT+F1) — изборът ѝ се маха като при „никога", а
  // масата остава избрана, свободна.
  const selectedBillClosedEmpty =
    selectedBill !== null &&
    isClosedEmptyBill(selectedBill) &&
    sortedBills.some((bill) => bill.id === selectedBill.id);

  // Масата следва сметката, избрана отвън (вкл. преименувана/разделена) —
  // само показана: непоказаната (чужда при лична номерация, маха се) не дърпа
  // масата си, иначе тя би си избрала друга сметка със същото име. Закритата
  // без бон не е на маса — за нея важи правилото по-горе.
  const selectedBillShown =
    selectedBill !== null &&
    (isClosedEmptyBill(selectedBill) || sortedBills.some((bill) => bill.id === selectedBill.id));
  useEffect(() => {
    if (!active || !selectedBill) return;
    if (!selectedBillShown) {
      setSelectedTableId(null);
      return;
    }
    const ids = tableIdsOf(selectedBill);
    setSelectedTableId((current) =>
      current !== null && ids.includes(current) ? current : (ids[0] ?? null),
    );
    // Само при смяна на сметката/името ѝ (или закриването ѝ без бон) — не при
    // всяко опресняване.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selectedBillId, selectedBillName, selectedBillClosedEmpty, selectedBillShown, tableIdsOf]);

  // Свободна избрана маса, на която вече има отворена сметка → избира се.
  useEffect(() => {
    if (!active || selectedBillId !== null || selectedTableId === null) return;
    const ref = tableRefById.get(selectedTableId);
    if (!ref) return;
    const preferred = preferredBillOf(billsOf(ref.table), currentUserId);
    if (preferred) onSyncBill(preferred.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selectedBillId, selectedTableId, tableRefById, billsOf, currentUserId]);

  const selectedTable = selectedTableId !== null ? (tableRefById.get(selectedTableId) ?? null) : null;
  // Избраната сметка приема поръчки („Добави") — не е закрита без бон и не се
  // закрива в момента; иначе масата е за „Нова маса".
  const selectedBillTakesOrders =
    selectedBill !== null &&
    !isClosedEmptyBill(selectedBill) &&
    !closingBillIds.has(selectedBill.id);

  // Районът на екрана: този на избраната маса, иначе последно избраният.
  const selectedTableRegionId = selectedTable?.regionId ?? null;
  useEffect(() => {
    if (selectedTableRegionId !== null) setRegionId(selectedTableRegionId);
  }, [selectedTableRegionId]);
  const activeRegion =
    regions.find((region) => region.id === regionId) ?? regions[0] ?? null;

  const selectTable = (table: SchemaElement) => {
    setSelectedTableId(table.id);
    floor.refreshFloor();
    onSelectBill(preferredBillOf(billsOf(table), currentUserId)?.id ?? null);
  };

  const openTable = (table: SchemaElement) => {
    if (!canOpenBill || !table.name) return selectTable(table);
    const preferred = preferredBillOf(billsOf(table), currentUserId);
    if (!preferred && takenByHiddenBill(table)) return selectTable(table);
    setSelectedTableId(table.id);
    if (!preferred) {
      onSelectBill(null);
      onOpenNamedTable(table.name ?? "");
      return;
    }
    // Масата, чието закриване чака принтера, не приема поръчки (като „Добави").
    if (closingBillIds.has(preferred.id)) return onSelectBill(preferred.id);
    onOpenBill(preferred.id);
  };

  /** Временна маса или сметка от панела на масата. */
  const selectBill = (billId: number) => {
    floor.refreshFloor();
    onSelectBill(billId);
  };

  const clearSelection = () => {
    setSelectedTableId(null);
    onSelectBill(null);
  };

  // Друг сервитьор в полето. Лична номерация: първо се маха изборът (иначе
  // избраната сметка би върнала полето на своя сервитьор, а избраната маса би
  // си избрала сметка), после се показва новият. Обща: само фокусът се сменя,
  // изборът остава (чуждата сметка си е в залата). Същият — нищо.
  const waiterField = waiterChoice && {
    options: waiterChoice.options,
    selectedWaiterId: waiterChoice.selectedWaiterId,
    onOpen: waiterChoice.refresh,
    onChange: (waiterId: number) => {
      if (waiterId === waiterChoice.selectedWaiterId) return;
      if (waiterChoice.clearsSelection) clearSelection();
      waiterChoice.pick(waiterId);
    },
  };

  // Масите, които светят: избраната и (за сметка на резервация) другите ѝ.
  const highlightedTableIds = useMemo(() => {
    const ids = new Set<number>(selectedBill ? tableIdsOf(selectedBill) : []);
    if (selectedTableId !== null) ids.add(selectedTableId);
    return ids;
  }, [selectedBill, selectedTableId, tableIdsOf]);

  return {
    activeRegion,
    selectRegion: setRegionId,
    selectedTable,
    /** Сметките на избраната маса — за реда с карти в панела. */
    selectedTableBills: selectedTable ? billsOf(selectedTable.table) : [],
    /** Избраната сметка приема поръчки → активен е „Добави". */
    selectedBillTakesOrders,
    /** Маса за „Нова маса": избраната маса без сметка, която приема поръчки
     *  (свободна или сметката ѝ е в процес на закриване) и не е заета от
     *  невидима чужда сметка. */
    freeTable:
      selectedTable && !selectedBillTakesOrders && !takenByHiddenBill(selectedTable.table)
        ? selectedTable.table
        : null,
    highlightedTableIds,
    /** Полето „Сервитьор"; null — не се показва. */
    waiterField,
    selectTable,
    openTable,
    selectBill,
    clearSelection,
  };
};

export type SchemaSelection = ReturnType<typeof useSchemaSelection>;
